import { AnyAction } from 'redux';
import { DataForDashboard, GlobalActions } from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import { BasisAdresse, Immobilie, Risikoort, VN_ADRESSE_ID } from '@ps20-frontend/frontend-models';
import { Adresse } from '@ps20/bff-models';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { DashboardActions } from '../dashboard.actions';

export const immobilienReducer = function (
  sectionState: Array<Immobilie> = null,
  action: AnyAction,
): Array<Immobilie> {
  switch (action.type) {
    case GlobalActions.AMIS_IMMOBILIEN_TRANSFORMED: {
      const actionPayload: Array<Immobilie> = action.payload;
      const newState = cloneDeep(actionPayload);

      const vnAdresseTransformed: Immobilie = sectionState.find(
        immobilie => immobilie.id === VN_ADRESSE_ID,
      );
      if (
        vnAdresseTransformed &&
        immoAdressenNotContainVnAdresse(vnAdresseTransformed.risikoort, actionPayload)
      ) {
        newState.push(vnAdresseTransformed);
      }

      return newState;
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const vnAdresse: Adresse = get(action.payload, 'versicherungsnehmer.adresse', null);
      if (vnAdresse) {
        sectionState.push(transformVnAdresseToImmobilie(vnAdresse));
      }
      return sectionState;
    }
    case DashboardActions.IMMO_AUSGEWAEHLT: {
      return sectionState.map(immobilie => {
        const newImmobilie = cloneDeep(immobilie);
        newImmobilie.ausgewaehlt = immobilie.id === action.payload;
        return newImmobilie;
      });
    }
    case DashboardActions.NEUE_IMMO_AUSGEWAEHLT: {
      const selectedImmo = sectionState.find(immo => immo.ausgewaehlt === true);
      if (selectedImmo) {
        selectedImmo.ausgewaehlt = false;
      }
      return sectionState;
    }
    case DashboardActions.NEW_IMMOBILIEN_RECEIVED: {
      const actionPayload: Array<Immobilie> = action.payload;
      return cloneDeep(actionPayload);
    }
    case GlobalActions.IN_DASHBOARD_UEBERNEHMEN: {
      const { versicherterRisikoort, nebengebaeudeQM }: DataForDashboard = action.payload;
      if (nebengebaeudeQM == null || !versicherterRisikoort || !sectionState) {
        return sectionState;
      }
      return sectionState.map(immobilie => {
        if (!isSameAddress(immobilie.risikoort, versicherterRisikoort)) {
          return immobilie;
        }
        return {
          ...immobilie,
          nebengebaeudeVorhanden: true,
          nebengebaeudeQM,
        };
      });
    }
    default:
      return sectionState;
  }
};

export const isSameAddress = function (a: BasisAdresse, b: BasisAdresse): boolean {
  return (
    !!a &&
    !!b &&
    a.plz === b.plz &&
    a.ort === b.ort &&
    a.strasse === b.strasse &&
    a.hausnummer === b.hausnummer
  );
};

export const immoAdressenNotContainVnAdresse = function (
  adresseVn: Risikoort,
  gebaeude: Array<Immobilie>,
): boolean {
  const gebaeudeWithVnAdresse = gebaeude
    .map(immobilie => immobilie.risikoort)
    .filter(adresse => adresse !== null)
    .some(adresse => isDeepEqualWithoutOrtsteil(adresseVn, adresse));

  return !gebaeudeWithVnAdresse;
};

export const isDeepEqualWithoutOrtsteil = function (
  adresseVn: Risikoort,
  adresse: Risikoort,
): boolean {
  const deepcopyAdresseVn: Risikoort = cloneDeep(adresseVn);

  delete deepcopyAdresseVn.ortsteil;
  delete deepcopyAdresseVn.hausnrzus;
  delete adresse.ortsteil;
  delete adresse.hausnrzus;

  return isEqual(deepcopyAdresseVn, adresse);
};

const adresseToRisikoort = function (adresse: Adresse): Risikoort {
  return {
    ort: get(adresse, 'ort', null),
    strasse: get(adresse, 'strasse', null),
    hausnrzus: get(adresse, 'hausnrzus', null),
    hausnummer: get(adresse, 'hausnummer', null),
    land: get(adresse, 'land', null),
    plz: get(adresse, 'plz', null),
    ortsteil: get(adresse, 'ortsteil', null),
    status: adresse.validierungsStatus,
  };
};

export const transformRisikoortToImmobilie = function (
  risikoort: Risikoort,
  id: string,
): Immobilie {
  return transformRisikoortToImmobilieWithBaujahr(risikoort, null, id);
};
export const transformRisikoortToImmobilieWithBaujahr = function (
  risikoort: Risikoort,
  baujahr: number,
  id: string,
): Immobilie {
  return {
    id: id,
    risikoort: risikoort,
    wohnflaeche: null,
    baujahr: baujahr,
    ausgewaehlt: false,
    kellerVorhanden: null,
    kellerMitWohnflaeche: null,
    flachdach: null,
    ferienhaus: null,
    nebengebaeudeVorhanden: null,
    nebengebaeudeQM: null,
    oeltank: null,
    gebaeudeArtKdv: null,
  };
};

export const transformVnAdresseToImmobilie = function (adresseFromAmis: Adresse): Immobilie {
  return transformRisikoortToImmobilieWithBaujahr(
    adresseToRisikoort(adresseFromAmis),
    null,
    VN_ADRESSE_ID,
  );
};
